import React from "react";
import { Outlet, useLocation } from "react-router";
import { useSelector } from "../../../../../../shared/src/store";
import { trans } from "../../../../../../shared/src/i18n/translate";
import { useInnerPageHooks } from "../../../../../../shared/src/layouts/useInnerPageHooks";
import { useMobileSidebarCloseOnMenuClick } from "../../../../../../shared/src/layouts/useMobileSidebarCloseOnMenuClick";
import { ROUTES } from "../../../../../../shared/src/constants";
import { getHubRoute } from "../../../../../../shared/src/handlers";
import { useAuthLayout } from "../../../../../../shared/src/hooks/useAuthLayout";

import PageTop, { faWrench } from "../../../../../../shared/src/components/blocks/PageTop";
import SidebarLateral from "../../../../../../shared/src/components/blocks/SidebarLateral";
import CentralArea from "../../../../../../shared/src/components/blocks/CentralArea";
import { BreadcrumbSection } from "../../../../../../shared/src/components/Breadcrumb";
import { SidebarMenuItem } from "../../../../../../shared/src/components/blocks/SidebarLateral/types";
import { whiteLabelSideMenuBuilder } from "../../../components/menu/side/Builder";

import { DEFAULT_SIDEBAR_LOGO } from "../../../../../../shared/src/constants/assets";
import { useSmartNavigate } from "../../../../../../shared/src/smartNav/useSmartNavigate";
import type { TReducers } from "../../../../../../shared/src/store/reducers";

import * as S from "./styles";

const toSidebarMenuItems = (items: SidebarMenuItem[]): SidebarMenuItem[] =>
  items.map((item) => ({
    title: item.title,
    icon: item.icon,
    path: item.path,
    active: item.active,
    onClick: item.onClick,
    isSubMenu: item.isSubMenu,
    subMenuItems: item.subMenuItems,
    accordionOpen: item.accordionOpen,
  }));

type InnerPageBaseProps = {
  filterMenuItems?: (items: SidebarMenuItem[]) => SidebarMenuItem[];
};

export const InnerPageBase = ({ filterMenuItems }: InnerPageBaseProps): React.JSX.Element => {
  const {
    user,
    isSidebarOpen,
    setIsSidebarOpen,
    menuTopItems,
    isFormPage,
  } = useInnerPageHooks();
  const {
    logoUrlWhiteNoLabel,
    sidebarTopLogoCollapsedUrl,
    sidebarTopLogoWidth,
    sidebarTopLogoHeight,
    sidebarTopLogoCollapsedWidth,
    sidebarTopLogoCollapsedHeight,
  } = useAuthLayout();
  const smartNavigate = useSmartNavigate();
  const routerLocation = useLocation();
  const featureType = useSelector((state: TReducers) =>
    state.auth.permissions?.features?.map((feature) => feature.code)
  );
  const builtMenuItems = whiteLabelSideMenuBuilder.build(routerLocation.pathname, featureType);
  const menuItems = filterMenuItems ? filterMenuItems(builtMenuItems) : builtMenuItems;

  useMobileSidebarCloseOnMenuClick(setIsSidebarOpen);

  const TOP_MENU_ITEMS = [
    {
      title: trans("Configurações"),
      icon: faWrench,
      path: getHubRoute(ROUTES.HUB.USER_CONFIG),
    },
  ];

  const pageTopUser = {
    ...user,
    company: { ...user.company, name: menuTopItems.userSection.company },
  };

  return (
    <div>
      <SidebarLateral
        menuItems={toSidebarMenuItems(menuItems)}
        isOpen={isSidebarOpen}
        onToggle={setIsSidebarOpen}
        logo={DEFAULT_SIDEBAR_LOGO}
        topLogo={{
          expanded: logoUrlWhiteNoLabel,
          collapsed: sidebarTopLogoCollapsedUrl,
          onClick: () => smartNavigate(getHubRoute(ROUTES.HUB.DASHBOARD)),
          expandedWidth: sidebarTopLogoWidth,
          expandedHeight: sidebarTopLogoHeight,
          collapsedWidth: sidebarTopLogoCollapsedWidth,
          collapsedHeight: sidebarTopLogoCollapsedHeight,
        }}
      />
      <S.FixedPageTopWrapper $isSidebarOpen={isSidebarOpen}>
        <PageTop
          user={pageTopUser}
          logoutAction={menuTopItems.userSection.logoutAction}
          menuItems={TOP_MENU_ITEMS}
        />
      </S.FixedPageTopWrapper>
      <CentralArea isOpen={isSidebarOpen} isFormPage={isFormPage}>
        <BreadcrumbSection />
        <Outlet />
      </CentralArea>
    </div>
  );
};

export const InnerPage = (): React.JSX.Element => <InnerPageBase />;
